import {
  AnalyticsApi,
  AnalyticsContextValue,
  AnalyticsEvent,
  IdentityApi,
} from '@backstage/core-plugin-api';
import { Config } from '@backstage/config';
import {
  enableActivityTracking,
  newTracker,
  setUserId,
  trackPageView,
} from '@snowplow/browser-tracker';
import {
  LinkClickTrackingPlugin,
  trackLinkClick,
} from '@snowplow/browser-plugin-link-click-tracking';
import { ErrorTrackingPlugin } from '@snowplow/browser-plugin-error-tracking';
import {
  SiteTrackingPlugin,
  trackSiteSearch,
} from '@snowplow/browser-plugin-site-tracking';
import { compare } from '@appfolio/backstage-plugin-utils-common';

const ALLOWED_BACKSTAGE_CONTEXT = [
  'entityRef',
  'pageRatingScore',
  'routeRef',
  'semanticSearchQuery',
  'semanticSearchResultLocation',
  'semanticSearchResultOwnerRef',
  'semanticSearchSessionId',
];

type UserIdTransform =
  | 'none'
  | 'sha-256'
  | ((userEntityRef: string) => Promise<string>);

/**
 * Simple hash function; relies on web cryptography + the sha-256 algorithm.
 *
 * @param value value to be hashed
 */
const hash = async (value: string): Promise<string> => {
  const digest = await window.crypto.subtle.digest(
    'sha-256',
    new TextEncoder().encode(value),
  );
  const hashArray = Array.from(new Uint8Array(digest));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
};

const isPageReload = () => {
  const timings = window.performance.getEntriesByType(
    'navigation',
  ) as PerformanceNavigationTiming[];
  return timings.some(t => t.type === 'reload');
};

const getPageUrlComponents = () =>
  window.location.pathname.split('/').filter(Boolean);

const getExtension = (context: AnalyticsContextValue) => {
  const extensionId = context.extensionId ?? context.extension;
  return extensionId ? String(extensionId) : 'App';
};

export class SnowplowClient implements AnalyticsApi {
  public static new(
    config: Config,
    identityApi: IdentityApi,
    options?: {
      userIdTransform?: UserIdTransform;
    },
  ) {
    const snowplowConfig = config.getConfig('app.analytics.snowplow');
    const collectorUrl = snowplowConfig.getString('collectorUrl');
    return new SnowplowClient(collectorUrl, identityApi, {
      ...options,
      enabled: snowplowConfig.getOptionalBoolean('enabled'),
      activityTrackingMinimumVisitLength: snowplowConfig.getOptionalNumber(
        'activityTrackingMinimumVisitLength',
      ),
      activityTrackingHeartbeatDelay: snowplowConfig.getOptionalNumber(
        'activityTrackingHeartbeatDelay',
      ),
    });
  }

  private userEmail?: string;

  private constructor(
    collectorUrl: string,
    identityApi: IdentityApi,
    options?: {
      enabled?: boolean;
      userIdTransform?: UserIdTransform;
      activityTrackingMinimumVisitLength?: number;
      activityTrackingHeartbeatDelay?: number;
    },
  ) {
    const {
      enabled = false,
      userIdTransform = 'sha-256',
      activityTrackingMinimumVisitLength: minimumVisitLength = 30,
      activityTrackingHeartbeatDelay: heartbeatDelay = 10,
    } = options ?? {};
    if (enabled) {
      identityApi.getBackstageIdentity().then(async identity => {
        newTracker('sp1', collectorUrl, {
          appId: 'backstage',
          postPath: '/io.augur.snowplow/1-0-0',
          plugins: [
            LinkClickTrackingPlugin(),
            ErrorTrackingPlugin(),
            SiteTrackingPlugin(),
          ],
        });
        let userId: string;
        if (typeof userIdTransform === 'function') {
          userId = await userIdTransform(identity.userEntityRef);
        } else if (userIdTransform === 'sha-256') {
          userId = await hash(identity.userEntityRef);
        } else {
          userId = identity.userEntityRef;
        }
        setUserId(userId);
        const profile = await identityApi.getProfileInfo();
        this.userEmail = profile.email;
        enableActivityTracking({ minimumVisitLength, heartbeatDelay });
      });
    }
  }

  public captureEvent(event: AnalyticsEvent) {
    const {
      action,
      subject,
      value,
      attributes = {},
      context: analyticsContext,
    } = event;
    const extension = getExtension(analyticsContext);
    const context = [
      {
        schema: 'iglu:io.augur.snowplow/identity_lite/jsonschema/1-0-0',
        data: {
          email: this.userEmail,
          is_page_reload: isPageReload(),
          pageURLComponents: getPageUrlComponents(),
          backstage_context: {
            extension,
            ...Object.fromEntries(
              Object.entries({
                ...analyticsContext,
                ...attributes,
              }).filter(([key]) => ALLOWED_BACKSTAGE_CONTEXT.includes(key)),
            ),
          },
        },
      },
    ];
    if (action === 'navigate' && compare(extension, 'app')) {
      trackPageView({
        context,
      });
      return;
    }
    if (action === 'click') {
      trackLinkClick({
        context,
        targetUrl: attributes?.to ? String(attributes.to) : '#',
        elementContent: subject,
      });
      return;
    }
    if (action === 'search') {
      trackSiteSearch({
        context,
        terms: [subject],
        totalResults: Number(value),
      });
      return;
    }
  }
}
